/**
 * Navigation drawer functionality for mobile menu
 * Handles opening/closing the drawer and accessibility
 */

const hamburgerBtn = document.querySelector<HTMLButtonElement>(".hamburger-btn");
const navDrawer = document.getElementById("nav-drawer");
const navOverlay = document.querySelector<HTMLElement>(".nav-overlay");
const mobileNavQuery = window.matchMedia("(max-width: 768px)");
const focusableSelector =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
let previouslyFocusedElement: HTMLElement | null = null;

function isDrawerOpen(): boolean {
  return hamburgerBtn?.getAttribute("aria-expanded") === "true";
}

function getDrawerFocusableElements(): HTMLElement[] {
  if (!hamburgerBtn || !navDrawer) return [];

  return [
    hamburgerBtn,
    ...Array.from(
      navDrawer.querySelectorAll<HTMLElement>(focusableSelector),
    ),
  ];
}

function syncDrawerInertState(): void {
  if (!navDrawer) return;
  navDrawer.inert = mobileNavQuery.matches && !isDrawerOpen();
}

function openDrawer(): void {
  if (!hamburgerBtn || !navDrawer || !navOverlay) return;

  previouslyFocusedElement =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : hamburgerBtn;
  hamburgerBtn.setAttribute("aria-expanded", "true");
  navDrawer.inert = false;
  navDrawer.classList.add("is-open");
  navOverlay.classList.add("is-visible");
  document.body.classList.add("nav-open");

  navDrawer.querySelector<HTMLElement>(focusableSelector)?.focus();
}

function closeDrawer(): void {
  if (!hamburgerBtn || !navDrawer || !navOverlay) return;

  hamburgerBtn.setAttribute("aria-expanded", "false");
  navDrawer.classList.remove("is-open");
  navOverlay.classList.remove("is-visible");
  document.body.classList.remove("nav-open");
  syncDrawerInertState();

  (previouslyFocusedElement || hamburgerBtn).focus();
  previouslyFocusedElement = null;
}

function toggleDrawer(): void {
  const isOpen = hamburgerBtn?.getAttribute("aria-expanded") === "true";
  if (isOpen) {
    closeDrawer();
  } else {
    openDrawer();
  }
}

// Toggle on hamburger click
hamburgerBtn?.addEventListener("click", toggleDrawer);

// Close on overlay click
navOverlay?.addEventListener("click", closeDrawer);

// Close on Escape key
document.addEventListener("keydown", (event) => {
  if (!isDrawerOpen()) return;

  if (event.key === "Escape") {
    event.preventDefault();
    closeDrawer();
    return;
  }

  if (event.key !== "Tab") return;

  const focusableElements = getDrawerFocusableElements();
  const firstElement = focusableElements[0];
  const lastElement = focusableElements.at(-1);
  if (!firstElement || !lastElement) return;

  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  } else if (
    !focusableElements.includes(document.activeElement as HTMLElement)
  ) {
    event.preventDefault();
    firstElement.focus();
  }
});

// Close drawer when clicking a nav link
navDrawer?.querySelectorAll("a").forEach((link) => {
  link.addEventListener("click", closeDrawer);
});

mobileNavQuery.addEventListener("change", () => {
  if (!mobileNavQuery.matches) {
    hamburgerBtn?.setAttribute("aria-expanded", "false");
    navDrawer?.classList.remove("is-open");
    navOverlay?.classList.remove("is-visible");
    document.body.classList.remove("nav-open");
    previouslyFocusedElement = null;
  }

  syncDrawerInertState();
});

syncDrawerInertState();
